/* REACT */
import { useState } from 'react';

/* ROUTER */
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

/* PLUGINS */
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';

/* SERVICES */
import { authService } from '../services/authService';

/* STORE */
import { useAuthStore } from '@/store/authStore';

/* SCHEMAS */
import { resetPasswordSchema, type ResetPasswordFormData } from '../schemas';

/* COMPONENTS */
import { AuthLayout } from './AuthLayout';

const REDIRECT_DELAY_MS = 3000;
const AUTH_CARD_BASE_CLASS = [
    'w-full max-w-[480px] rounded-lg border border-[#e5e5e5] bg-white',
    'flex flex-col items-center px-8 py-8 md:px-10 text-center',
].join(' ');
const INPUT_CLASS = [
    'self-stretch rounded-lg border border-[#e5e5e5] bg-white px-3 py-2',
    'text-sm outline-none focus:border-brand disabled:cursor-not-allowed',
].join(' ');
const SUBMIT_BUTTON_CLASS = [
    'self-stretch rounded-lg bg-brand flex items-center justify-center px-4 py-3',
    'cursor-pointer text-white border-none text-sm leading-5 tracking-[0.25px]',
    'font-medium hover:bg-brand-hover disabled:bg-[#e5e5e5]',
    'disabled:text-[rgba(46,27,20,0.4)]',
].join(' ');

/**
 * DOCU: Renders PO-style reset password form. <br>
 * Triggered: User opens /reset-password?token=... from email. <br>
 * Last Updated Date: May 19, 2026 <br>
 * @returns {JSX.Element} Reset password screen.
 * @author Sam
 */
export const ResetPasswordForm = () => {
    const navigate = useNavigate();
    const logout = useAuthStore((state) => state.logout);
    const [search_params] = useSearchParams();
    const reset_token = search_params.get('token');
    const [is_loading, setIsLoading] = useState(false);
    const [is_success, setIsSuccess] = useState(false);
    const [error_message, setErrorMessage] = useState('');
    const form = useForm<ResetPasswordFormData>({
        resolver: zodResolver(resetPasswordSchema),
    });

    /**
     * DOCU: Submit new password with reset token. <br>
     * Triggered: Reset password form submit. <br>
     * Last Updated Date: May 19, 2026 <br>
     * @param {ResetPasswordFormData} data - New password payload.
     * @returns {Promise<void>}
     * @author Sam
     */
    const onSubmit = async (data: ResetPasswordFormData): Promise<void> => {
        if (!reset_token) {
            setErrorMessage('Invalid or missing reset token');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await authService.resetPassword({
                token: reset_token,
                ...data,
            });

            if (response.result?.is_reset) {
                logout();
                setIsSuccess(true);
                setTimeout(() => navigate('/login'), REDIRECT_DELAY_MS);
                return;
            }

            setErrorMessage('Failed to reset password. Please try again.');
        }
        catch {
            setErrorMessage('An error occurred. Please request a new reset link.');
        }
        finally {
            setIsLoading(false);
        }
    };

    if (!reset_token) {
        return (
            <AuthLayout title="">
                <div className={`${AUTH_CARD_BASE_CLASS} gap-6`}>
                    <XCircle className="size-16 text-[#dc2626]" aria-hidden="true" />
                    <h1 className="text-[28px] leading-9 font-medium text-[#363636]">
                        Invalid Reset Link
                    </h1>
                    <p className="text-base leading-6 tracking-[0.5px] text-[#525252]">
                        This password reset link is invalid or has expired.
                    </p>
                    <Link to="/forgot-password" className="text-sm text-brand hover:underline">
                        Request a new reset link
                    </Link>
                </div>
            </AuthLayout>
        );
    }

    if (is_success) {
        return (
            <AuthLayout title="">
                <div className={`${AUTH_CARD_BASE_CLASS} gap-6`}>
                    <CheckCircle2 className="size-16 text-[#16a34a]" aria-hidden="true" />
                    <h1 className="text-[28px] leading-9 font-medium text-[#363636]">
                        Password Reset Successfully!
                    </h1>
                    <p className="text-base leading-6 tracking-[0.5px] text-[#525252]">
                        You can now sign in with your new password.
                    </p>
                    <p className="text-sm text-[#737373]">Redirecting to sign in...</p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout title="">
            <div className={`${AUTH_CARD_BASE_CLASS} gap-8`}>
                <div className="self-stretch flex flex-col items-center gap-2">
                    <h1 className="self-stretch text-[28px] leading-9 font-medium text-[#363636]">
                        Reset Your Password
                    </h1>
                    <p className="self-stretch text-base leading-6 tracking-[0.5px] text-[#525252]">
                        Enter your new password below.
                    </p>
                </div>

                <form
                    className="self-stretch flex flex-col items-start gap-4 text-left"
                    onSubmit={form.handleSubmit(onSubmit)}
                >
                    <div className="self-stretch flex flex-col items-start gap-2">
                        <label
                            htmlFor="new_password"
                            className={[
                                'self-stretch text-sm leading-5 tracking-[0.25px]',
                                'font-medium text-[#281e1b]',
                            ].join(' ')}
                        >
                            New Password
                        </label>
                        <input
                            id="new_password"
                            type="password"
                            placeholder="Enter new password"
                            disabled={is_loading}
                            className={INPUT_CLASS}
                            {...form.register('new_password')}
                        />
                        {form.formState.errors.new_password && (
                            <div className="text-xs leading-4 text-[#dc2626]">
                                {form.formState.errors.new_password.message}
                            </div>
                        )}
                    </div>

                    <div className="self-stretch flex flex-col items-start gap-2">
                        <label
                            htmlFor="confirm_password"
                            className={[
                                'self-stretch text-sm leading-5 tracking-[0.25px]',
                                'font-medium text-[#281e1b]',
                            ].join(' ')}
                        >
                            Confirm Password
                        </label>
                        <input
                            id="confirm_password"
                            type="password"
                            placeholder="Confirm new password"
                            disabled={is_loading}
                            className={INPUT_CLASS}
                            {...form.register('confirm_password')}
                        />
                        {form.formState.errors.confirm_password && (
                            <div className="text-xs leading-4 text-[#dc2626]">
                                {form.formState.errors.confirm_password.message}
                            </div>
                        )}
                    </div>

                    {error_message && (
                        <div
                            className={[
                                'self-stretch p-3 bg-[#fee2e2] border border-[#dc2626]',
                                'rounded-lg text-sm leading-5 text-[#dc2626]',
                            ].join(' ')}
                        >
                            {error_message}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={is_loading}
                        className={SUBMIT_BUTTON_CLASS}
                    >
                        {is_loading ? 'Resetting Password...' : 'Reset Password'}
                    </button>
                </form>

                <Link
                    to="/login"
                    className="text-sm text-[#737373] hover:text-[#171717] hover:underline"
                >
                    Back to Sign In
                </Link>
            </div>
        </AuthLayout>
    );
};
